import { useState, useCallback, useEffect, useRef } from 'react';
import { LanguageCode } from '../types/types';
import { supabase } from '../services/supabaseAudioService';

export interface SentenceRevisionInfo {
  original: string;
  french: string;
  english?: string;
  audio?: string;
  category: string;
  timestamp: number;
  nextReview: number;
  type: 'sentence';
  words: string[];
  isCorrect: boolean;
}

interface RevisionProgress {
  sentences: SentenceRevisionInfo[];
  lastReviewed: number | null;
  learnedSentences: Set<string>;
}

interface SessionStats {
  total: number;
  correct: number;
  accuracy: number;
  isComplete: boolean;
}

interface SessionState {
  isComplete: boolean;
  stats: SessionStats | null;
  reviewedWords?: SentenceRevisionInfo[];
}

interface SentenceRevisionState {
  revisionProgress: RevisionProgress;
  sessionHistory: SentenceRevisionInfo[];
  isInitialized: boolean;
  sessionState: SessionState;
}

interface UseSentenceRevisionOptions {
  readOnly?: boolean;
  lazyInit?: boolean;
}

const useSentenceRevision = (
  languageCode: LanguageCode, 
  options: UseSentenceRevisionOptions = {}
) => {
  const { readOnly = false, lazyInit = false } = options;
  
  const [state, setState] = useState<SentenceRevisionState>({
    revisionProgress: {
      sentences: [],
      lastReviewed: null,
      learnedSentences: new Set<string>()
    },
    sessionHistory: [],
    isInitialized: false,
    sessionState: {
      isComplete: false,
      stats: null
    }
  });

  const [isLoading, setIsLoading] = useState(false);
  const initializationRef = useRef<boolean>(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const initializeRevision = useCallback(async () => {
    if (initializationRef.current || state.isInitialized) {
     
      return;
    }

    
    initializationRef.current = true;
    setIsLoading(true);

    try {
      // 1. Charger depuis localStorage principal
      const localKey = `sentence-revision-${languageCode}-sessionHistory`;
      const localData = localStorage.getItem(localKey);
      
      let loadedSentences: SentenceRevisionInfo[] = [];
      
      if (localData) {
        try {
          const parsed = JSON.parse(localData);
          if (Array.isArray(parsed)) {
            loadedSentences = parsed.filter((item: any) => 
              item.type === 'sentence' && 
              item.original && item.original.trim() !== '' &&
              item.french && item.french.trim() !== ''
            );
           
          }
        } catch (error) {
}
      }

      // 2. Charger depuis les données de progression de phrases
      const progressKey = `sentence-progress-${languageCode}`;
      const progressData = localStorage.getItem(progressKey);
      
      if (progressData) {
        try {
          const progressSessions = JSON.parse(progressData);
          if (Array.isArray(progressSessions)) {
          
            
            const progressSentences: SentenceRevisionInfo[] = [];
            progressSessions.forEach((session: any) => {
              if (session.masteredSentences && Array.isArray(session.masteredSentences)) {
                session.masteredSentences.forEach((sentence: any) => {
                  if (sentence.original && sentence.french && sentence.mastered) {
                    progressSentences.push({
                      original: sentence.original,
                      french: sentence.french,
                      audio: sentence.audio,
                      category: sentence.category || session.category || 'Salutations',
                      timestamp: sentence.timestamp || Date.now(),
                      nextReview: (sentence.timestamp || Date.now()) + (24 * 60 * 60 * 1000),
                      type: 'sentence',
                      words: sentence.words || sentence.original.split(' '),
                      isCorrect: true
                    });
                  }
                });
              }
            });
            
         
            
            // Fusionner avec les phrases existantes
            const mergedSentences = [...loadedSentences];
            progressSentences.forEach(newSentence => {
              const existingIndex = mergedSentences.findIndex(s => 
                s.original === newSentence.original && s.category === newSentence.category
              );
              
              if (existingIndex >= 0) {
                mergedSentences[existingIndex] = {
                  ...mergedSentences[existingIndex],
                  ...newSentence,
                  timestamp: Math.max(mergedSentences[existingIndex].timestamp, newSentence.timestamp)
                };
              } else {
                mergedSentences.push(newSentence);
              }
            });
            
            loadedSentences = mergedSentences;
         
          }
        } catch (error) {
}
      }

      // 3. 🆕 NOUVEAU: Charger depuis Supabase (au lieu de Google Drive)
      if (!readOnly) {
        try {
          
          
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session?.user) {
           
            
            const { data, error } = await supabase
              .from('user_progress')
              .select('progress_data')
              .eq('user_id', session.user.id)
              .eq('language_code', languageCode)
              .maybeSingle();
            
            if (error) {
} else if (data?.progress_data?.exercises?.sentenceConstruction) {
              const sentenceData = data.progress_data.exercises.sentenceConstruction;
             
              
              if (sentenceData.sentences && Array.isArray(sentenceData.sentences)) {
        
                
                // Fusionner avec local
                const mergedSentences = [...loadedSentences];
                sentenceData.sentences.forEach((newSentence: any) => {
                  const existingIndex = mergedSentences.findIndex(s => 
                    s.original === newSentence.original && s.category === newSentence.category
                  );
                  
                  if (existingIndex >= 0) {
                    // Garder la version la plus récente
                    if (newSentence.timestamp > mergedSentences[existingIndex].timestamp) {
                    
                      mergedSentences[existingIndex] = {
                        ...mergedSentences[existingIndex],
                        ...newSentence,
                        timestamp: Math.max(mergedSentences[existingIndex].timestamp, newSentence.timestamp)
                      };
                    }
                  } else {
                 
                    mergedSentences.push(newSentence);
                  }
                });
                
                loadedSentences = mergedSentences;
                
                
                // Sauvegarder immédiatement en localStorage
                try {
                  localStorage.setItem(localKey, JSON.stringify(loadedSentences));
                
                } catch (saveError) {
}
              } else {
              
              }
              
              // Charger aussi depuis progress si disponible
              if (sentenceData.progress && Array.isArray(sentenceData.progress)) {
                
                
                const progressSentences: SentenceRevisionInfo[] = [];
                sentenceData.progress.forEach((session: any) => {
                  if (session.masteredSentences && Array.isArray(session.masteredSentences)) {
                    session.masteredSentences.forEach((sentence: any) => {
                      if (sentence.original && sentence.french && sentence.mastered) {
                        progressSentences.push({
                          original: sentence.original,
                          french: sentence.french,
                          audio: sentence.audio,
                          category: sentence.category || session.category || 'Salutations',
                          timestamp: sentence.timestamp || Date.now(),
                          nextReview: (sentence.timestamp || Date.now()) + (24 * 60 * 60 * 1000),
                          type: 'sentence',
                          words: sentence.words || sentence.original.split(' '),
                          isCorrect: true
                        });
                      }
                    });
                  }
                });
                
                if (progressSentences.length > 0) {
                
                  
                  // Fusionner
                  const finalMerged = [...loadedSentences];
                  progressSentences.forEach(newSentence => {
                    const existingIndex = finalMerged.findIndex(s => 
                      s.original === newSentence.original && s.category === newSentence.category
                    );
                    
                    if (existingIndex < 0) {
                      finalMerged.push(newSentence);
                    }
                  });
                  
                  loadedSentences = finalMerged;
                 
                }
              }
            } else {
             
            }
          } else {
          
          }
        } catch (supabaseError) {
        }
      }

      // 4. Mise à jour de l'état
      setState(prev => ({
        ...prev,
        revisionProgress: {
          sentences: loadedSentences,
          lastReviewed: loadedSentences.length > 0 ? Math.max(...loadedSentences.map(s => s.timestamp)) : null,
          learnedSentences: new Set(loadedSentences.filter(s => s.isCorrect).map(s => s.original))
        },
        sessionHistory: loadedSentences,
        isInitialized: true
      }));

      // 5. Sauvegarder le résultat fusionné en localStorage
      if (loadedSentences.length > 0) {
        try {
          localStorage.setItem(localKey, JSON.stringify(loadedSentences));
          
        } catch (error) {
}
      }

     

    } catch (error) {
} finally {
      setIsLoading(false);
    }
  }, [languageCode, readOnly, state.isInitialized]);

  // ✅ NOUVEAU: Écouter l'événement sentenceProgressSynced
  useEffect(() => {
    const handleSentenceProgressSynced = (event: CustomEvent) => {
     
      
      if (event.detail.language !== languageCode) {
        
        return;
      }
      
      if (event.detail.sessions && Array.isArray(event.detail.sessions)) {
        const sentences: SentenceRevisionInfo[] = [];
        
        event.detail.sessions.forEach((session: any) => {
          if (session.masteredSentences && Array.isArray(session.masteredSentences)) {
            session.masteredSentences.forEach((sentence: any) => {
              if (sentence.original && sentence.french && sentence.mastered) {
                sentences.push({
                  original: sentence.original,
                  french: sentence.french,
                  audio: sentence.audio,
                  category: sentence.category || session.category || 'Salutations',
                  timestamp: sentence.timestamp || Date.now(),
                  nextReview: (sentence.timestamp || Date.now()) + (24 * 60 * 60 * 1000),
                  type: 'sentence',
                  words: sentence.words || sentence.original.split(' '),
                  isCorrect: true
                });
              }
            });
          }
        });
        
        if (sentences.length > 0) {
        
          
          setState(prev => {
            const existingSentences = prev.sessionHistory;
            const newSentences = [...existingSentences];
            
            sentences.forEach(newSentence => {
              const existingIndex = newSentences.findIndex(s => 
                s.original === newSentence.original && s.category === newSentence.category
              );
              
              if (existingIndex >= 0) {
                newSentences[existingIndex] = {
                  ...newSentences[existingIndex],
                  ...newSentence,
                  timestamp: Math.max(newSentences[existingIndex].timestamp, newSentence.timestamp)
                };
              } else {
                newSentences.push(newSentence);
              }
            });
            
            // Sauvegarder immédiatement en localStorage
            const localKey = `sentence-revision-${languageCode}-sessionHistory`;
            try {
              localStorage.setItem(localKey, JSON.stringify(newSentences));
            
            } catch (error) {
}
            
            return {
              ...prev,
              sessionHistory: newSentences,
              revisionProgress: {
                sentences: newSentences,
                lastReviewed: Date.now(),
                learnedSentences: new Set(newSentences.filter(s => s.isCorrect).map(s => s.original))
              },
              isInitialized: true
            };
          });
        }
      }
    };

    window.addEventListener('sentenceProgressSynced', handleSentenceProgressSynced as EventListener);
    
    return () => {
      window.removeEventListener('sentenceProgressSynced', handleSentenceProgressSynced as EventListener);
    };
  }, [languageCode]);

  // ✅ Initialisation conditionnelle au montage
  useEffect(() => {
    if (!lazyInit && !readOnly) {
   
      initializeRevision();
    } else {
      
    }
  }, [initializeRevision, lazyInit, readOnly]);

  // ✅ Fonction de sauvegarde locale
  const saveLocalProgress = useCallback((sentences: SentenceRevisionInfo[]) => {
    try {
      const localKey = `sentence-revision-${languageCode}-sessionHistory`;
      const dataToSave = sentences.slice(0, 3000); // Limiter à 3000 phrases max
      localStorage.setItem(localKey, JSON.stringify(dataToSave));
     
    } catch (error) {
}
  }, [languageCode]);

  // ✅ Ajouter des phrases à la révision
  const addSentencesToReview = useCallback(async (sentenceInfo: SentenceRevisionInfo) => {
    if (!state.isInitialized && !readOnly) {
    
      await initializeRevision();
    }

    // Charger les phrases existantes
    const localKey = `sentence-revision-${languageCode}-sessionHistory`;
    let existingSentences: SentenceRevisionInfo[] = [];
    
    try {
      const existingData = localStorage.getItem(localKey);
      if (existingData) {
        const parsed = JSON.parse(existingData);
        if (Array.isArray(parsed)) {
          existingSentences = parsed.filter((s: any) => 
            s.original && s.original.trim() !== '' &&
            s.french && s.french.trim() !== '' &&
            s.category && s.category.trim() !== ''
          );
        }
      }
    } catch (error) {
}

  
    
    const newSentences = [...existingSentences];
    
    // Vérifier si la phrase existe déjà
    const existingIndex = newSentences.findIndex(s => 
      s.original.trim().toLowerCase() === sentenceInfo.original.trim().toLowerCase() && 
      s.category === sentenceInfo.category
    );

    if (existingIndex >= 0) {
      // Mettre à jour la phrase existante
      const existingSentence = newSentences[existingIndex];
      newSentences[existingIndex] = {
        ...existingSentence,
        ...sentenceInfo,
        timestamp: Math.max(existingSentence.timestamp, sentenceInfo.timestamp),
        isCorrect: sentenceInfo.isCorrect || existingSentence.isCorrect
      };
     
    } else {
      // Ajouter une nouvelle phrase
      if (!sentenceInfo.original?.trim() || !sentenceInfo.french?.trim()) {
return;
      }
      
      const newSentence = {
        ...sentenceInfo,
        timestamp: Date.now(),
        nextReview: Date.now() + (24 * 60 * 60 * 1000)
      };
      
      newSentences.unshift(newSentence);
   
    }

    // Filtrer et limiter les phrases
    const validSentences = newSentences.filter(s => 
      s.original && s.original.trim() !== '' &&
      s.french && s.french.trim() !== '' &&
      s.category && s.category.trim() !== ''
    ).slice(0, 3000);

    // Mettre à jour l'état
    setState(prev => ({
      ...prev,
      sessionHistory: validSentences,
      revisionProgress: {
        sentences: validSentences,
        lastReviewed: Date.now(),
        learnedSentences: new Set(validSentences.filter(s => s.isCorrect).map(s => s.original))
      }
    }));

    // Sauvegarder en localStorage
    try {
      localStorage.setItem(localKey, JSON.stringify(validSentences));
   
    } catch (error) {
}

 

  }, [state.isInitialized, readOnly, initializeRevision, languageCode]);

  // ✅ Vérifier si une phrase est apprise
  const isLearnedSentence = useCallback((original: string): boolean => {
    return state.sessionHistory.some(s => 
      s.original === original && s.isCorrect
    );
  }, [state.sessionHistory]);

  // ✅ Mettre à jour le progrès de révision
  const updateRevisionProgress = useCallback(async (
    sentenceOrWord: SentenceRevisionInfo | string, 
    isCorrect: boolean,
    category?: string,
    options?: {
      translation?: string;
      audio?: string;
    }
  ) => {
    let sentenceInfo: SentenceRevisionInfo;
    
    if (typeof sentenceOrWord === 'string') {
      sentenceInfo = {
        original: sentenceOrWord,
        french: options?.translation || '',
        audio: options?.audio,
        category: category || 'Révision',
        timestamp: Date.now(),
        nextReview: Date.now() + (isCorrect ? 24 * 60 * 60 * 1000 : 10 * 60 * 1000),
        type: 'sentence',
        words: sentenceOrWord.split(' '),
        isCorrect: isCorrect
      };
    } else {
      sentenceInfo = {
        ...sentenceOrWord,
        isCorrect: isCorrect,
        timestamp: Date.now(),
        nextReview: Date.now() + (isCorrect ? 24 * 60 * 60 * 1000 : 10 * 60 * 1000)
      };
    }
    
    await addSentencesToReview(sentenceInfo);
  }, [addSentencesToReview]);

  // ✅ Terminer une session
  const finishSession = useCallback(async (options?: {
    showStats?: boolean;
    lastSentenceInfo?: any;
  }) => {
    const stats: SessionStats = {
      total: state.sessionHistory.length,
      correct: state.sessionHistory.filter(s => s.isCorrect).length,
      accuracy: state.sessionHistory.length > 0 ? 
        (state.sessionHistory.filter(s => s.isCorrect).length / state.sessionHistory.length) * 100 : 0,
      isComplete: true
    };

    setState(prev => ({
      ...prev,
      sessionState: {
        isComplete: true,
        stats: stats,
        reviewedWords: state.sessionHistory
      }
    }));

    return {
      stats,
      isComplete: true,
      showStats: options?.showStats ?? true,
      reviewedWords: state.sessionHistory
    };
  }, [state.sessionHistory]);

  // ✅ Réinitialiser une session
  const resetSession = useCallback(() => {
    setState(prev => ({
      ...prev,
      sessionState: {
        isComplete: false,
        stats: null
      }
    }));
  }, []);

  // ✅ Obtenir l'historique de session
  const getSessionHistory = useCallback((): SentenceRevisionInfo[] => {
    return [...state.sessionHistory];
  }, [state.sessionHistory]);

  // ✅ Nettoyer les timeouts au démontage
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    // Fonctions principales
    addSentencesToReview,
    isLearnedSentence,
    getSessionHistory,
    updateRevisionProgress,
    finishSession,
    resetSession,
    
    // État
    revisionProgress: state.revisionProgress,
    sessionState: state.sessionState,
    isLoading,
    isInitialized: state.isInitialized,
    
    // Fonctions utilitaires
    initializeRevision,
    saveLocalProgress
  };
};

export default useSentenceRevision;